/**
 * Seat Service
 * ============
 * Handles seat and client limit checking for subscription tiers.
 */

import { supabase } from '../lib/supabase'
import { stripeConfig, type TierId, type TierKey } from '../config/stripe.config'

export interface SeatSummary {
  current_seats: number
  max_seats: number
  seats_available: number
  is_at_limit: boolean
  tier: string
}

export interface ClientSummary {
  current_clients: number
  max_clients: number
  clients_available: number
  is_at_limit: boolean
  tier: string
}

// Type-safe RPC wrapper - these functions exist in DB but types aren't regenerated
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (fn: string, params: Record<string, unknown>) => supabase.rpc(fn as any, params as any)

export const seatService = {
  /**
   * Get seat usage summary for an organization
   */
  async getOrganizationSeatSummary(organizationId: string): Promise<SeatSummary | null> {
    const { data, error } = await rpc('get_organization_seat_summary', { org_id: organizationId })

    if (error) {
      console.error('Failed to get organization seat summary:', error)
      return null
    }

    const results = data as unknown as SeatSummary[] | null
    if (!results || results.length === 0) {
      return null
    }

    return results[0]
  },

  /**
   * Get client usage summary for a provider
   */
  async getProviderClientSummary(providerId: string): Promise<ClientSummary | null> {
    const { data, error } = await rpc('get_provider_client_summary', { provider_user_id: providerId })

    if (error) {
      console.error('Failed to get provider client summary:', error)
      return null
    }

    const results = data as unknown as ClientSummary[] | null
    if (!results || results.length === 0) {
      return null
    }

    return results[0]
  },

  /**
   * Check if organization can add a new member
   */
  async canAddOrganizationMember(organizationId: string): Promise<boolean> {
    const { data, error } = await rpc('can_add_organization_member', { org_id: organizationId })

    if (error) {
      console.error('Failed to check organization seat limit:', error)
      return false
    }

    return data === true
  },

  /**
   * Check if provider can add a new client
   */
  async canAddClient(providerId: string): Promise<boolean> {
    const { data, error } = await rpc('can_add_client', { provider_user_id: providerId })

    if (error) {
      console.error('Failed to check client limit:', error)
      return false
    }

    return data === true
  },

  /**
   * Get seat limit for a tier (client-side lookup)
   */
  getTierSeatLimit(tierId: TierId | string): number {
    const tierKey = tierId.toUpperCase() as TierKey
    const tier = stripeConfig.tiers[tierKey]
    return tier?.limits.coachSeats ?? 0
  },

  /**
   * Get client limit for a tier (client-side lookup)
   */
  getTierClientLimit(tierId: TierId | string): number {
    const tierKey = tierId.toUpperCase() as TierKey
    const tier = stripeConfig.tiers[tierKey]
    return tier?.limits.maxClients ?? 0
  },

  /**
   * Get the tier that allows the specified number of seats
   */
  getRequiredTierForSeats(seatsNeeded: number): TierId | null {
    const tierKeys: TierKey[] = ['FREE', 'BASIC', 'ADVANCED', 'CORPORATE', 'PLATINUM']

    for (const tierKey of tierKeys) {
      const tier = stripeConfig.tiers[tierKey]
      const limit = tier.limits.coachSeats
      if (limit === -1 || limit >= seatsNeeded) {
        return tier.id
      }
    }

    return null
  },

  /**
   * Get the tier that allows the specified number of clients
   */
  getRequiredTierForClients(clientsNeeded: number): TierId | null {
    const tierKeys: TierKey[] = ['FREE', 'BASIC', 'ADVANCED', 'CORPORATE', 'PLATINUM']

    for (const tierKey of tierKeys) {
      const tier = stripeConfig.tiers[tierKey]
      const limit = tier.limits.maxClients
      if (limit === -1 || limit >= clientsNeeded) {
        return tier.id
      }
    }

    return null
  },

  /**
   * Format seat limit for display (-1 = Unlimited)
   */
  formatLimit(limit: number): string {
    return limit === -1 ? 'Unlimited' : limit.toString()
  },

  /**
   * Get usage percentage (0-100, capped at 100)
   */
  getUsagePercentage(current: number, max: number): number {
    if (max === -1) return 0 // Unlimited
    if (max === 0) return 100 // At limit
    return Math.min(100, Math.round((current / max) * 100))
  },

  /**
   * Get usage status color based on percentage
   */
  getUsageStatusColor(current: number, max: number): 'green' | 'yellow' | 'red' {
    if (max === -1) return 'green' // Unlimited
    const percentage = this.getUsagePercentage(current, max)
    if (percentage >= 100) return 'red'
    if (percentage >= 80) return 'yellow'
    return 'green'
  },
}
